import React, {
  useCallback,
  createContext,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

const FREE_TRIAL_STATUS = {
  AWAITING_VERIFICATION: 'AwaitingVerification',
  EMAIL_CONFIRMED: 'EmailConfirmed',
  SITE_CREATED: 'SiteCreated',
};

export const PAGE_STATE = Object.freeze({
  LANDING_PAGE: '/',
  FAIL_SAFE_FORM: '/form',
  CHECK_YOUR_EMAIL: '/check-your-email',
  EMAIL_VERIFIED: '/email-verified',
  BASIC_INFO_FORM: '/basic-info',
  ERROR_USER_ALREADY_EXISTS: '/error/user-already-exists',
  ERROR_SITE_ALREADY_CREATED: '/error/site-already-created',
  ERROR_ANGLE_BRACKETS: '/error/no-angle-brackets-in-password',
  ERROR_SWIPECLOCK_EMAIL: '/error/swipeclock-email',
  ERROR_EMAIL_ALREADY_EXISTS: '/error/email-already-exists',
  ERROR_DEFAULT: '/error',
  SITE_CREATING: '/site-creating',
  SITE_READY: '/site-ready',
});

// Values match what the API expects
export const FORM_IDS = Object.freeze({
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  COMPANY_NAME: 'companyName',
  BUSINESS_EMAIL: 'email',
  PHONE_NUMBER: 'phone',
  NUMBER_OF_EMPLOYEES: 'numberOfEmployees',
});

export const BASIC_INFO_FORM_IDS = Object.freeze({
  COMPANY_NAME: 'companyName',
  STATE: 'state',
  TIME_ZONE: 'timeZone',
  PASSWORD: 'password',
  CONFIRM_PASSWORD: 'confirmPassword',
});

const FreeTrialContext = createContext();

export function useFreeTrialContext() {
  return useContext(FreeTrialContext);
}

const initialState = {
  registrationToken: null,
  userToken: null,
  status: FREE_TRIAL_STATUS.AWAITING_VERIFICATION,
  currentPage: PAGE_STATE.LANDING_PAGE,
  errors: {
    [PAGE_STATE.FAIL_SAFE_FORM]: {},
    [PAGE_STATE.BASIC_INFO_FORM_PAGE]: {},
  },
  formBag: {
    [PAGE_STATE.FAIL_SAFE_FORM]: {},
    [PAGE_STATE.BASIC_INFO_FORM_PAGE]: {},
  },
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_REGISTRATION_TOKEN':
      return {
        ...state,
        registrationToken: action.payload,
      };
    case 'SET_USER_TOKEN':
      return {
        ...state,
        userToken: action.payload,
      };
    case 'EMPTY_FORM_BAG':
      return {
        ...state,
        formBag: initialState.formBag,
      };
    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };
    case 'SET_ERRORS':
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.page]: {
            ...(state.errors[action.payload.page] || {}),
            [action.payload.error.id]: action.payload.error.message,
          },
        },
      };
    case 'SET_FORM_BAG':
      return {
        ...state,
        formBag: {
          ...state.formBag,
          [action.payload.page]: {
            ...action.payload.values,
          },
        },
      };
    case 'SET_STATUS':
      return {
        ...state,
        status: action.payload,
      };
    default:
      return state;
  }
};

export default function FreeTrialProvider({ children, defaultValues: bag }) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    formBag: bag ?? initialState.formBag,
  });

  const { pages, totalPages } = useMemo(() => {
    return {
      pages: PAGE_STATE,
      totalPages: Object.keys(PAGE_STATE).length,
    };
  }, [Object.keys(PAGE_STATE).length]);

  const setCurrentPage = useCallback((page) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page });
  }, []);

  const setError = useCallback((id, error, page) => {
    dispatch({
      type: 'SET_ERRORS',
      payload: { error: { id, message: error }, page },
    });
  }, []);

  const setFormValues = useCallback((values, page) => {
    dispatch({ type: 'SET_FORM_BAG', payload: { values, page } });
  }, []);

  const setRegistrationToken = useCallback((token) => {
    dispatch({ type: 'SET_REGISTRATION_TOKEN', payload: token });
  }, []);

  const setUserToken = useCallback((token) => {
    dispatch({ type: 'SET_USER_TOKEN', payload: token });
  }, []);

  const setStatus = useCallback((status) => {
    dispatch({ type: 'SET_STATUS', payload: status });
  }, []);

  const reset = useCallback(() => {
    // Clear Errors
    dispatch({ type: 'SET_ERRORS', payload: initialState.errors });
    // Empty formBags
    dispatch({ type: 'EMPTY_FORM_BAG' });
    // Set current page to landing page
    setCurrentPage(PAGE_STATE.LANDING_PAGE);
    setRegistrationToken(null);
    setStatus(FREE_TRIAL_STATUS.AWAITING_VERIFICATION);
  }, []);

  // Note on useMemo:
  // The state reference remains the same across renders
  // So using "state" is suitable for useHook dependency arrays
  // like useMemo here.
  const value = useMemo(
    () => ({
      state,
      actions: {
        reset,
        setCurrentPage,
        setError,
        setFormValues,
        setRegistrationToken,
        setUserToken,
        setStatus,
      },
      meta: {
        pages,
        status: FREE_TRIAL_STATUS,
        totalPages,
      },
    }),
    [state]
  );

  return (
    <FreeTrialContext.Provider value={value}>
      {children}
    </FreeTrialContext.Provider>
  );
}

FreeTrialProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaultValues: PropTypes.object,
};

// Helper method to easily navigate to pages
// and side-effect updates current page in context
// using History replace.
export function useFreeTrialPageNavigation() {
  const { actions } = useFreeTrialContext();
  const navigate = useNavigate();

  const navigateToPage = (pageState) => {
    const pageStates = Object.values(PAGE_STATE);
    if (pageStates.includes(pageState)) {
      actions.setCurrentPage(pageState);
      navigate(`/freeTrial${pageState}`, { replace: true });
    } else {
      console.error(`Unknown page state: ${pageState} and KEY:`);
      console.error(`${JSON.stringify(PAGE_STATE, null, 2)}`);
    }
  };

  return navigateToPage;
}
